import tenantAxiosInstance from "api/tenantAxiosInstance";
import { useGetCascadeProcessQuery } from "api/TenantSlug/CascadeProcess/hooks";
import { useGetDefaultsQuery } from "api/TenantSlug/Setting/hooks";
import { CascadeChoice } from "components/UI/Cascade/CascadeChoice.tsx";
import { CascadeFailed } from "components/UI/Cascade/CascadeFailed.tsx";
import { CascadeLoading } from "components/UI/Cascade/CascadeLoading.tsx";
import { CascadePriorityRedirect } from "components/UI/Cascade/CascadePriorityRedirect.tsx";
import { CascadeProcessing } from "components/UI/Cascade/CascadeProcessing.tsx";
import { CascadeRedirect } from "components/UI/Cascade/CascadeRedirect.tsx";
import { useEffect, useState } from "react";
import { Route } from "routes/$tenantSlug/processes/_layout/$processId";
import { CascadeProcessItemAction, CascadeProcessItemStatus, CascadeProcessStatusResponse } from "types/enums.ts";
import { redirectToUrl } from "utils/redirectToUrl";

import GetCascadeProcessResponse = App.Http.Responses.Cascade.GetCascadeProcessResponse;
import GetConversionApiExternalFinalPageResponse = App.Http.Responses.Conversion.GetConversionApiExternalFinalPageResponse;

const EXTERNAL_FINAL_PAGE_STATUS_CODE = 202;

interface CascadeProps {
    processId: string;
}

enum CascadeScreenType {
    Loading = "Loading",
    Processing = "Processing",
    Failed = "Failed",
    PriorityRedirect = "PriorityRedirect",
    Redirect = "Redirect",
    Choice = "Choice",
}

export const Cascade = ({ processId }: CascadeProps) => {
    const { tenantSlug } = Route.useParams();

    const getTenantQuery = useGetDefaultsQuery(tenantSlug, {
        gcTime: 0,
        staleTime: 0,
    });

    if (getTenantQuery.data?.tenant) {
        tenantAxiosInstance.setTenantSlugInUrl(getTenantQuery.data.tenant.slug_name);
    }

    const cascadeProcessQueryEnabled = getTenantQuery.data?.tenant ? true : false; //todo add not enabled if process action is not processing

    const [cascadeScreenType, setCascadeScreenType] = useState<CascadeScreenType>(CascadeScreenType.Loading);

    const getCascadeProcessQuery = useGetCascadeProcessQuery(processId, tenantSlug, {
        refetchInterval: (query: { state: { status: string } }) => (query.state.status === "error" ? false : 500),
        retry: false,
        enabled:
            cascadeProcessQueryEnabled &&
            (cascadeScreenType === CascadeScreenType.Processing || cascadeScreenType === CascadeScreenType.Loading),
        gcTime: 0,
        staleTime: 0,
    });
    const cascade = getCascadeProcessQuery.data?.data;
    const isExternalFinalPage = getCascadeProcessQuery.data?.status === EXTERNAL_FINAL_PAGE_STATUS_CODE;
    const externalFinalPageCascade = isExternalFinalPage
        ? (cascade as GetConversionApiExternalFinalPageResponse)
        : undefined;
    const normalCascade = cascade && !isExternalFinalPage ? (cascade as GetCascadeProcessResponse) : undefined;

    useEffect(() => {
        if (externalFinalPageCascade) {
            const cascade = externalFinalPageCascade;

            if (!cascade.is_finished || cascade.status === CascadeProcessItemStatus.Processing) {
                setCascadeScreenType(CascadeScreenType.Processing);
                return;
            }

            if (cascade.url) {
                redirectToUrl(cascade.url);
                return;
            }

            setCascadeScreenType(CascadeScreenType.Failed);
            return;
        }

        if (cascade) {
            if (cascade.status === CascadeProcessStatusResponse.Processing) {
                setCascadeScreenType(CascadeScreenType.Processing);
            } else if (cascade.status === CascadeProcessStatusResponse.Completed) {
                setCascadeScreenType(getCascadeScreenType(cascade as GetCascadeProcessResponse));
            } else if (cascade.status === CascadeProcessStatusResponse.Failed) {
                setCascadeScreenType(CascadeScreenType.Failed);
            }
        }
    }, [cascade]);

    useEffect(() => {
        if (getCascadeProcessQuery.isError) {
            setCascadeScreenType(CascadeScreenType.Failed);
        }
    }, [getCascadeProcessQuery.isError]);

    const getCascadeScreenType = (cascade: GetCascadeProcessResponse) => {
        if (cascade.items) {
            if (cascade.items?.some((item) => item.action === CascadeProcessItemAction.Processing)) {
                return CascadeScreenType.Processing;
            }

            // Check for PriorityRedirect with valid URL
            const priorityRedirectItems = cascade.items?.filter(
                (item) => item.action === CascadeProcessItemAction.PriorityRedirect
            );
            if (priorityRedirectItems && priorityRedirectItems.length > 0) {
                // Check if any priority redirect has a valid URL
                const validPriorityRedirect = priorityRedirectItems.find((item) => item.url !== null);

                if (validPriorityRedirect) {
                    // At least one priority redirect has a valid URL
                    return CascadeScreenType.PriorityRedirect;
                } else if (priorityRedirectItems.length === 1) {
                    // Only one priority redirect and it has null URL - fail
                    return CascadeScreenType.Failed;
                }
                // Multiple priority redirects but all have null URLs - continue to next screen type
            }

            if (cascade.items?.some((item) => item.action === CascadeProcessItemAction.Choice)) {
                return CascadeScreenType.Choice;
            }

            if (
                cascade.items?.some((item) => item.action === CascadeProcessItemAction.Redirect) ||
                cascade.items?.some((item) => item.action === CascadeProcessItemAction.PostVerification) ||
                cascade.items?.some((item) => item.action === CascadeProcessItemAction.Visible)
            ) {
                return CascadeScreenType.Redirect;
            }
        }
        return CascadeScreenType.Failed;
    };

    let content;
    if (cascadeScreenType === CascadeScreenType.Loading) {
        content = <CascadeLoading />;
    } else if (cascadeScreenType === CascadeScreenType.Processing) {
        content = <CascadeProcessing />;
    } else if (cascadeScreenType === CascadeScreenType.PriorityRedirect && normalCascade) {
        content = <CascadePriorityRedirect cascade={normalCascade} processId={processId} tenantSlug={tenantSlug} />;
    } else if (cascadeScreenType === CascadeScreenType.Choice) {
        content = <CascadeChoice />;
    } else if (cascadeScreenType === CascadeScreenType.Redirect && normalCascade) {
        content = <CascadeRedirect cascade={normalCascade} processId={processId} tenantSlug={tenantSlug} />;
    } else if (cascadeScreenType === CascadeScreenType.Failed) {
        content = <CascadeFailed />;
    }

    return <div className={"flex justify-center items-center"}>{content}</div>;
};
